import useCompanies from '../../../hooks/companies/useCompanies';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import { FaEdit } from 'react-icons/fa';
import { GENERIC_MODAL_ACTIONS } from '../../../context/actionTypes';
import ListDisplayer from '../../../utils/ListDisplayer';
import {GiChoice, GiStopSign} from "react-icons/gi";
import { CgOptions } from 'react-icons/cg';
import { TbHttpDelete, TbUserPlus } from 'react-icons/tb';
import { Tooltip } from 'react-tooltip';
import { formatNumber, humanFileSize } from '../../../utils/Functions';
import { IoBagRemoveOutline } from 'react-icons/io5';
import useConfiguration from "../manageTechnical/inference/useConfiguration";


interface StatusPointBaseProps {
  statusPointClassName: string;
  tooltipText: string;
  text: string;
}

function StatusPointBase({statusPointClassName, text, tooltipText}: StatusPointBaseProps) {
  return <div
      className='flex items-center gap-2'
      data-tooltip-id='companies-tooltip'
      data-tooltip-content={tooltipText}
  >
    <div className={`rounded-full h-2 w-2 ${statusPointClassName}`}/>
    {text}
  </div>
}

interface CompanyStatusPointProps {
  status: boolean;
}

function CompanyStatusPoint({status}: CompanyStatusPointProps) {
  return status
      ? <StatusPointBase statusPointClassName="bg-response-positive" tooltipText="Entreprise active" text="Active"/>
      : <StatusPointBase statusPointClassName="bg-response-medium" tooltipText="Entreprise inactive" text="Inactive"/>;
}

export default function Companies() {
  const [{ companies }, { loadCompanies, createCompany }] = useCompanies(true,true);
  const [{configuration}] = useConfiguration();

  if (configuration === null) {
    // Prevent showing the companies table while the configuration is loading
    return <></>
  }

  const columnsOnSummary = [
    {
      label: 'Nom',
      key: 'name',
      className: 'font-semibold',
      colSpan: 2,
    },
    {
      label: 'Email',
      key: 'email',
      colSpan: 3,
    },
    /* TODO : Add column selection
    {
      label: 'Secteur',
      key: 'sector',
      colSpan: 2,
    },
    {
      label: 'Zone Géographique',
      key: 'geographical_zone',
      colSpan: 3,
    },*/
    {
      label: 'Tokens input',
      key: 'prompt_tokens',
      colSpan: 2,
      template: (data) =>
        isNaN(data.prompt_tokens) ? 0  : formatNumber(data.prompt_tokens),
    },
    {
      label: 'Tokens output',
      key: 'completion_tokens',
      colSpan: 2,
      template: (data) =>
        isNaN(data.completion_tokens) ? 0  : formatNumber(data.completion_tokens),
    },
    {
      label: "Tokens embedding",
      key: "embedding_tokens",
      colSpan: 2,
      template: (data) =>
          isNaN(data.embedding_tokens) ? 0  : formatNumber(data.embedding_tokens),
    },
    {
      label: 'Créée le',
      key: 'created_at',
      type: 'date',
      colSpan: 2,
    },
    {
      label: "Stockage",
      key: "int_size",
      colSpan: 1,
      template: (data) =>
        isNaN(data.int_size) ? "N/A" : humanFileSize(data.int_size),
    },

    {
      label: 'Status',
      key: 'status',
      colSpan: 1,
      template: (data) => (
        <CompanyStatusPoint status={data.status} />
      ),
    },
    {
      label: "Questions Chatbot",
      key: 'questions_enabled',
      colSpan: 2,
      template: (data) => {
        let statusPointClassName: string, mainText: string, tooltipText: string;
        switch (data.questions_enabled) {
          case true:
            statusPointClassName = "bg-response-positive";
            mainText = "Activées";
            tooltipText = "L'entreprise peut poser des questions au chatbot, remplace la valeur définie dans \"Outils de maintenance\".";
            break;
          case false:
            statusPointClassName = "bg-response-medium";
            mainText = "Désactivées";
            tooltipText = "L'entreprise ne peut pas poser de questions au chatbot, remplace la valeur définie dans \"Outils de maintenance\".";
            break;
          default: // Null
            mainText = "Par défaut";
            if (configuration.questions_enabled) {
              statusPointClassName = "bg-response-positive";
              tooltipText = "Valeur par défaut définie dans \"Outils de maintenance\" : l'entreprise peut poser des questions au chatbot.";
            } else {
              statusPointClassName = "bg-response-medium";
              tooltipText = "Valeur par défaut définie dans \"Outils de maintenance\" : l'entreprise ne peut pas poser de questions au chatbot.";
            }
            break;
        }

        return <StatusPointBase statusPointClassName={statusPointClassName} tooltipText={tooltipText} text={mainText}/>
      }
    },
    {
      label: "Upload de documents",
      key: 'documents_upload_enabled',
      colSpan: 2,
      template: (data) => {
        let statusPointClassName: string, mainText: string, tooltipText: string;
        switch (data.documents_upload_enabled) {
          case true:
            statusPointClassName = "bg-response-positive";
            mainText = "Activés";
            tooltipText = "L'entreprise peut ajouter ou réessayer d'ajouter des documents, remplace la valeur définie dans \"Outils de maintenance\".";
            break;
          case false:
            statusPointClassName = "bg-response-medium";
            mainText = "Désactivés";
            tooltipText = "L'entreprise ne peut pas ajouter ou réessayer d'ajouter des documents, remplace la valeur définie dans \"Outils de maintenance\".";
            break;
          default: // Null
            mainText = "Par défaut";
            if (configuration.documents_upload_enabled) {
              statusPointClassName = "bg-response-positive";
              tooltipText = "Valeur par défaut définie dans \"Outils de maintenance\" : l'entreprise peut ajouter ou réessayer d'ajouter des documents.";
            } else {
              statusPointClassName = "bg-response-medium";
              tooltipText = "Valeur par défaut définie dans \"Outils de maintenance\" : l'entreprise ne peut pas ajouter ou réessayer d'ajouter des documents."
            }
            break;
        }

        return <StatusPointBase statusPointClassName={statusPointClassName} tooltipText={tooltipText} text={mainText}/>
      }
    },
  ];

  const actionsMenu = [
    {
      action: GENERIC_MODAL_ACTIONS.MANAGE_COMPANY_PLAN,
      label: 'Gérer le plan',
      enabled: (e) => true,
      Icon: CgOptions,
    },
    {
      action: GENERIC_MODAL_ACTIONS.MANAGE_COMPANY_OPTIONS,
      label: 'Gérer les options',
      enabled: (e) => true,
      Icon: GiChoice,
    },
    {
      action: GENERIC_MODAL_ACTIONS.MANAGE_COMPANY_SALES,
      label: 'Associer un commercial',
      enabled: (e) => true,
      Icon: TbUserPlus,
    },
    {
      action: GENERIC_MODAL_ACTIONS.CREATE_OR_UPDATE_COMPANY,
      label: 'Modifier',
      enabled: (e) => true,
      Icon: FaEdit,
    },
    {
      action: GENERIC_MODAL_ACTIONS.UPDATE_COMPANY_RESTRICTIONS,
      label: "Modifier les restrictions",
      enabled: (e) => true,
      Icon: GiStopSign,
    },
    {
      action: GENERIC_MODAL_ACTIONS.DELETE_REQUESTS,
      label: 'Réinitilialiser le plan',
      enabled: (e) => true,
      Icon: TbHttpDelete,
    },
    {
      action: GENERIC_MODAL_ACTIONS.DELETE_SENSITIVE_DATA_REQUESTS,
      label: 'Supprimer les données sensibles des requêtes',
      enabled: (e) => true,
      Icon: IoBagRemoveOutline,
    },
    // {TODO Delete Company}
    // {
    //   action: GENERIC_MODAL_ACTIONS.DELETE_COMPANY,
    //   label: 'Supprimer',
    //   enabled: (e) => true,
    //   Icon: FiTrash,
    // },
  ];

  return (
    <div className='pt-4 px-4 text-left flex flex-col max-h-screen overflow-hidden text-base '>
      <div className='flex'>
        <button
          className='btn-transition bg-gray-600 hover:bg-opacity-75 mb-4'
          onClick={() => createCompany()}
        >
          <AiOutlinePlusCircle className='h-5 w-5' />
          <div>Ajouter une entreprise</div>
        </button>
      </div>
      <ListDisplayer
        columnsOnSummary={columnsOnSummary}
        items={companies}
        reload={loadCompanies}
        actionsMenu={actionsMenu}
        defaultSortKey='created_at'
      />
      <Tooltip
        id='companies-tooltip'
        style={{ fontSize: '0.75rem', zIndex: 9999 }}
        place='left'
      />
    </div>
  );
}
